import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { COMPANY } from "../data/site";

/**
 * Per-page SEO via DOM manipulation. Sets:
 *  - <title>
 *  - <meta name="description">
 *  - <link rel="canonical"> (self-referencing on primary domain)
 *  - <meta property="og:title|og:description|og:url|og:type|og:image">
 *  - <meta name="twitter:card|title|description|image">
 *  - <html lang="sv">
 *
 * Lighter alternative to <SEO /> Helmet wrapper, used by existing pages.
 */
function setMeta(name, content, attr = "name") {
  if (content == null) return;
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(url) {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", url);
}

export default function usePageMeta({ title, description, image, type = "website" } = {}) {
  const location = useLocation();
  useEffect(() => {
    if (document.documentElement.lang !== "sv") {
      document.documentElement.lang = "sv";
    }
    if (title) document.title = title;
    if (description) setMeta("description", description);

    const path = location.pathname + (location.search || "");
    const canonical = `${COMPANY.primaryDomain}${path}`;
    setCanonical(canonical);
    const og = image
      ? image.startsWith("http")
        ? image
        : `${COMPANY.primaryDomain}${image}`
      : `${COMPANY.primaryDomain}/images/hero.webp`;

    if (title) {
      setMeta("og:title", title, "property");
      setMeta("twitter:title", title);
    }
    if (description) {
      setMeta("og:description", description, "property");
      setMeta("twitter:description", description);
    }
    setMeta("og:url", canonical, "property");
    setMeta("og:type", type, "property");
    setMeta("og:image", og, "property");
    setMeta("og:site_name", COMPANY.name, "property");
    setMeta("og:locale", "sv_SE", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", og);
  }, [title, description, image, type, location.pathname, location.search]);
}
